"use client";

import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth";
import {
  useProfileNotes,
  useCreateProfileNote,
  useUpdateProfileNote,
  useDeleteProfileNote,
  useSetNoteReaction,
  useDeleteNoteReaction,
  profileNotesQueryKey,
  type ProfileNote,
  type ProfileNoteAuthor,
  type NotesScope,
} from "@/hooks/use-profile-notes";
import {
  useCreateConversationNote,
  useUpdateConversationNote,
  useDeleteConversationNote,
  useSetConversationNoteReaction,
  useDeleteConversationNoteReaction,
} from "@/features/conversations/hooks/use-notes";
import { NoteSyncHint } from "@/features/ats-sync/components/note-sync-hint";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import type {
  ConversationNoteOut,
  ConversationNotesGroup,
  JobProfileNoteListResponse,
} from "@/lib/api/model";
import { NoteCard, type NoteLike } from "./note-cards";
import { MentionEditor } from "./mention-editor";
import { MentionResolverProvider } from "./mention-resolver";

function toNoteLikeFromProfile(note: ProfileNote): NoteLike {
  return {
    id: note.id,
    author: note.author
      ? note.author
      : note.ats_author_name
        ? {
            first_name: note.ats_author_name,
            last_name: "",
            email: "",
            id: "",
            avatar_url: null,
          }
        : null,
    author_user_id: note.author_user_id,
    content: note.content,
    depth: note.depth,
    like_count: note.like_count,
    dislike_count: note.dislike_count,
    viewer_reaction: note.viewer_reaction,
    replies: (note.replies ?? []).map(toNoteLikeFromProfile),
    created_at: note.created_at,
  };
}

function toNoteLikeFromConversation(note: ConversationNoteOut): NoteLike {
  return {
    id: note.id,
    author: note.author ?? null,
    author_user_id: note.author_user_id ?? null,
    content: note.content,
    depth: note.depth,
    like_count: note.like_count ?? 0,
    dislike_count: note.dislike_count ?? 0,
    viewer_reaction: note.viewer_reaction ?? null,
    replies: (note.replies ?? []).map(toNoteLikeFromConversation),
    created_at: note.created_at,
  };
}

function interviewLinkLabel(scheduledStart: string): string {
  const date = new Date(scheduledStart).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `Interview, ${date}`;
}

type FeedItem =
  | { kind: "job"; created_at: string; note: ProfileNote }
  | {
      kind: "interview";
      created_at: string;
      note: ConversationNoteOut;
      group: ConversationNotesGroup;
    };

function buildFeed(
  scope: NotesScope,
  notes: ProfileNote[],
  conversationNotes: ConversationNotesGroup[],
): FeedItem[] {
  const jobItems: FeedItem[] = notes.map((note) => ({
    kind: "job",
    created_at: note.created_at,
    note,
  }));

  if (scope.kind !== "job") return jobItems;

  const interviewItems: FeedItem[] = conversationNotes.flatMap((group) =>
    (group.notes ?? []).map((note) => ({
      kind: "interview" as const,
      created_at: note.created_at,
      note,
      group,
    })),
  );

  return [...jobItems, ...interviewItems].sort((a, b) => {
    if (a.created_at < b.created_at) return -1;
    if (a.created_at > b.created_at) return 1;
    if (a.kind === b.kind) return 0;
    return a.kind === "job" ? -1 : 1;
  });
}

function ProfileNoteThread({
  note,
  scope,
  profileId,
  currentUserId,
  currentUser,
  isOrgAdmin,
  sourceSearchId,
  atsName,
}: {
  note: ProfileNote;
  scope: NotesScope;
  profileId: string | null;
  currentUserId: string | null;
  currentUser: ProfileNoteAuthor | null;
  isOrgAdmin: boolean;
  sourceSearchId?: string | null;
  atsName?: string | null;
}) {
  const createNote = useCreateProfileNote(
    scope,
    profileId,
    currentUser,
    sourceSearchId,
  );
  const updateNote = useUpdateProfileNote(scope, profileId, sourceSearchId);
  const deleteNote = useDeleteProfileNote(scope, profileId);
  const setReaction = useSetNoteReaction(scope, profileId);
  const deleteReaction = useDeleteNoteReaction(scope, profileId);

  const isImported = note.source === "ats";
  const isOwner = !!currentUserId && note.author_user_id === currentUserId;
  const canEdit = !isImported && (isOwner || isOrgAdmin);
  const canWrite = !isImported;
  const like = toNoteLikeFromProfile(note);

  return (
    <div data-testid="note-card">
      <NoteCard
        note={like}
        canEdit={canEdit}
        canWrite={canWrite}
        currentUserId={currentUserId}
        isOrgAdmin={isOrgAdmin}
        viaLabel={isImported ? atsName || "ATS" : null}
        busy={createNote.isPending}
        editPending={updateNote.isPending}
        onReply={(parent, content) =>
          createNote.mutate({
            content,
            parent_note_id: parent.id,
            parent_depth: parent.depth,
          })
        }
        onEdit={(n, content, options) =>
          updateNote.mutate({ noteId: n.id, content }, options)
        }
        onDelete={(n) => deleteNote.mutate(n.id)}
        onReact={(n, value) => setReaction.mutate({ noteId: n.id, value })}
        onClearReaction={(n) => deleteReaction.mutate(n.id)}
      />
    </div>
  );
}

function InterviewNoteThread({
  note,
  group,
  jobId,
  profileId,
  currentUserId,
  isOrgAdmin,
}: {
  note: ConversationNoteOut;
  group: ConversationNotesGroup;
  jobId: string;
  profileId: string | null;
  currentUserId: string | null;
  isOrgAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const createNote = useCreateConversationNote(group.conversation_id);
  const updateNote = useUpdateConversationNote(group.conversation_id);
  const deleteNote = useDeleteConversationNote(group.conversation_id);
  const setReaction = useSetConversationNoteReaction(group.conversation_id);
  const deleteReaction = useDeleteConversationNoteReaction(
    group.conversation_id,
  );

  const invalidateJobNotes = useCallback(() => {
    if (!profileId) return;
    void queryClient.invalidateQueries({
      queryKey: profileNotesQueryKey({ kind: "job", jobId }, profileId),
    });
  }, [queryClient, jobId, profileId]);

  const canWrite = group.can_write;
  const isOwner = !!currentUserId && note.author_user_id === currentUserId;
  const canEdit = canWrite && (isOwner || isOrgAdmin);
  const like = toNoteLikeFromConversation(note);

  return (
    <div data-testid="note-card" className="flex w-full flex-col gap-1.5">
      <a
        href={`/conversations/${group.conversation_id}`}
        className="inline-flex w-fit items-center rounded bg-contrast/8 px-1.5 py-0.5 text-[11px] leading-4 text-contrast/60 hover:text-foreground"
      >
        {interviewLinkLabel(group.scheduled_start)}
      </a>
      <NoteCard
        note={like}
        canEdit={canEdit}
        canWrite={canWrite}
        currentUserId={currentUserId}
        isOrgAdmin={isOrgAdmin}
        busy={createNote.isPending}
        editPending={updateNote.isPending}
        onReply={(parent, content) =>
          createNote.mutate(
            { content, parent_note_id: parent.id },
            { onSuccess: invalidateJobNotes },
          )
        }
        onEdit={(n, content, options) =>
          updateNote.mutate(
            { noteId: n.id, content },
            {
              onSuccess: () => {
                invalidateJobNotes();
                options?.onSuccess?.();
              },
            },
          )
        }
        onDelete={(n) =>
          deleteNote.mutate(n.id, { onSuccess: invalidateJobNotes })
        }
        onReact={(n, value) =>
          setReaction.mutate(
            { noteId: n.id, value },
            { onSuccess: invalidateJobNotes },
          )
        }
        onClearReaction={(n) =>
          deleteReaction.mutate(n.id, { onSuccess: invalidateJobNotes })
        }
      />
    </div>
  );
}

export function NotesPanel({
  scope,
  profileId,
  sourceSearchId,
}: {
  scope: NotesScope;
  profileId: string | null;
  sourceSearchId?: string | null;
}) {
  const { user, isAdmin } = useAuth();
  const currentUserId = user?.id ?? null;
  const currentUser: ProfileNoteAuthor | null = user
    ? {
        id: user.id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        avatar_url: user.avatar_url ?? null,
      }
    : null;
  const { data, isLoading } = useProfileNotes(scope, profileId);
  const { isLinked, connectorName } = useJobAtsLink(
    scope.kind === "job" ? scope.jobId : null,
  );
  const atsName = isLinked ? connectorName : null;
  const createNote = useCreateProfileNote(
    scope,
    profileId,
    currentUser,
    sourceSearchId ?? null,
  );
  const [inputValue, setInputValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const submitNote = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;
      createNote.mutate({ content: trimmed });
      setInputValue("");
      setEditorKey((k) => k + 1);
    },
    [createNote],
  );

  const handleAddNote = useCallback(() => {
    submitNote(inputValue);
  }, [inputValue, submitNote]);

  const notes = (data?.notes ?? []) as ProfileNote[];
  const conversationNotes =
    scope.kind === "job"
      ? (((data as JobProfileNoteListResponse | undefined)
          ?.conversation_notes ?? []) as ConversationNotesGroup[])
      : [];

  const feed = useMemo(
    () => buildFeed(scope, notes, conversationNotes),
    [scope, notes, conversationNotes],
  );

  const mentionNotes = useMemo(() => {
    const interviewRoots = conversationNotes.flatMap((g) => g.notes ?? []);
    return [...notes, ...interviewRoots];
  }, [notes, conversationNotes]);

  return (
    <MentionResolverProvider notes={mentionNotes}>
      <div className="flex w-full flex-col gap-2.5 items-start px-4 py-4 sm:px-8 sm:py-6 border-b border-border">
        <div className="flex items-center py-1.5 w-full">
          <p className="text-lg font-medium text-foreground leading-normal whitespace-nowrap">
            Notes
          </p>
        </div>

        <div className="inline-grid w-full">
          <div
            className={cn(
              "relative min-h-[88px] w-full overflow-hidden rounded-lg border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
              inputFocused ? "border-contrast/25" : "border-border",
            )}
            onFocus={() => setInputFocused(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setInputFocused(false);
              }
            }}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-lg bg-contrast/6"
            />
            <div className="relative">
              <MentionEditor
                key={editorKey}
                placeholder="Click here to add a note to this profile (this will be visible to your team)"
                onChange={setInputValue}
                onSubmit={submitNote}
                minHeight={72}
              />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]" />
          </div>

          <div className="mt-2.5 flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm text-foreground/50">
                Use @ to mention team members
              </span>
              {scope.kind === "job" && <NoteSyncHint jobId={scope.jobId} />}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={handleAddNote}
              disabled={createNote.isPending || !inputValue.trim()}
            >
              Add Note
            </Button>
          </div>
        </div>

        {isLoading && <NotesSkeleton />}
        {!isLoading && feed.length > 0 && (
          <div className="flex w-full flex-col gap-2.5">
            {feed.map((item) =>
              item.kind === "job" ? (
                <ProfileNoteThread
                  key={`job-${item.note.id}`}
                  note={item.note}
                  scope={scope}
                  profileId={profileId}
                  currentUserId={currentUserId}
                  currentUser={currentUser}
                  isOrgAdmin={isAdmin}
                  sourceSearchId={sourceSearchId ?? null}
                  atsName={atsName}
                />
              ) : (
                <InterviewNoteThread
                  key={`interview-${item.note.id}`}
                  note={item.note}
                  group={item.group}
                  jobId={(scope as { kind: "job"; jobId: string }).jobId}
                  profileId={profileId}
                  currentUserId={currentUserId}
                  isOrgAdmin={isAdmin}
                />
              ),
            )}
          </div>
        )}
      </div>
    </MentionResolverProvider>
  );
}

const SK = "rounded bg-contrast/6 shimmer shimmer-bg text-foreground";

function NoteCardSkeleton() {
  return (
    <div className="flex w-full flex-col gap-3 overflow-hidden rounded-lg border border-border bg-contrast/3 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className={cn(SK, "size-[18px] rounded-[3.375px]")} />
          <div className={cn(SK, "h-3.5 w-24")} />
        </div>
        <div className={cn(SK, "h-3 w-10")} />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className={cn(SK, "h-3 w-full")} />
        <div className={cn(SK, "h-3 w-3/4")} />
      </div>
      <div className="flex items-center justify-between">
        <div className={cn(SK, "h-3 w-12")} />
        <div className="flex items-center gap-3">
          <div className={cn(SK, "size-4 !rounded")} />
          <div className={cn(SK, "size-4 !rounded")} />
          <div className={cn(SK, "size-4 !rounded")} />
        </div>
      </div>
    </div>
  );
}

function NotesSkeleton() {
  return (
    <div className="flex w-full flex-col gap-2.5">
      <NoteCardSkeleton />
      <NoteCardSkeleton />
    </div>
  );
}

export function ProjectNotesPanel({
  projectId,
  profileId,
  sourceSearchId,
}: {
  projectId: string | null;
  profileId: string | null;
  sourceSearchId?: string | null;
}) {
  if (!projectId) return null;
  return (
    <NotesPanel
      scope={{ kind: "project", projectId }}
      profileId={profileId}
      sourceSearchId={sourceSearchId}
    />
  );
}

export function JobNotesPanel({
  jobId,
  profileId,
}: {
  jobId: string;
  profileId: string | null;
}) {
  return <NotesPanel scope={{ kind: "job", jobId }} profileId={profileId} />;
}
